// src/pages/Sales.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import API from "../utils/api";
import Swal from "sweetalert2";
import bgImage from "../assets/airmax.webp";

/* --------------------------------- Utils -------------------------------- */
const normalize = (s = "") =>
  String(s).toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").trim();

const scoreProduct = (p, q) => {
  const n = normalize(p.name);
  const size = normalize(p.size || "");
  const color = normalize(p.color || "");
  const id = String(p.product_id);
  const qn = normalize(q);
  if (!qn) return 0;
  if (n.startsWith(qn)) return 1;
  if (size && size.startsWith(qn)) return 2;
  if (color && color.startsWith(qn)) return 3;
  if (n.includes(qn)) return 4;
  if (size && size.includes(qn)) return 5;
  if (color && color.includes(qn)) return 6;
  if (id.startsWith(qn)) return 7;
  if (id.includes(qn)) return 8;
  return 99;
};

const metaText = (p) => {
  const parts = [`${Number(p.price || 0).toLocaleString()} Kesh`, p.unit || "unit"];
  if (p.size) parts.push(`size ${p.size}`);
  if (p.color) parts.push(String(p.color));
  return parts.join(" • ");
};

const formatDate = (d) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
const formatTime = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;

/* -------------------------- Searchable Product UI ------------------------ */
function ProductSelect({ products, value, onChange, placeholder = "Select product…" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const id = "pc-visible-scrollbar";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      .pc-scroll{ scrollbar-width:auto; scrollbar-color:#9aa4b2 #eff2f6; }
      .pc-scroll::-webkit-scrollbar{ width:10px; }
      .pc-scroll::-webkit-scrollbar-track{ background:#eff2f6; border-radius:8px; }
      .pc-scroll::-webkit-scrollbar-thumb{ background:#9aa4b2; border-radius:8px; border:2px solid #eff2f6; }
      .pc-scroll::-webkit-scrollbar-thumb:hover{ background:#7b8794; }
    `;
    document.head.appendChild(style);
  }, []);

  const selected = useMemo(
    () => products.find((p) => Number(p.product_id) === Number(value)) || null,
    [products, value]
  );

  const filtered = useMemo(() => {
    const base = !query
      ? products
      : products
          .map((p) => ({ p, s: scoreProduct(p, query) }))
          .filter((x) => x.s < 99)
          .sort((a, b) => a.s - b.s || normalize(a.p.name).localeCompare(normalize(b.p.name)))
          .map((x) => x.p);
    return base.slice(0, 200);
  }, [products, query]);

  const handlePick = (p) => {
    onChange?.(p.product_id);
    setOpen(false);
    setQuery("");
    requestAnimationFrame(() => inputRef.current?.blur());
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      setActiveIdx(0);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIdx]) handlePick(filtered[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (
        !inputRef.current?.parentElement?.contains(e.target) &&
        !listRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selectedLabel = useMemo(() => {
    if (!selected) return null;
    const extras = [selected.size ? `size ${selected.size}` : null, selected.color || null]
      .filter(Boolean)
      .join(" • ");
    return extras ? `${selected.name} — ${extras}` : selected.name;
  }, [selected]);

  return (
    <div className="relative w-full">
      <div
        className="flex items-center gap-2 border rounded px-2 py-2 bg-white cursor-text"
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        <input
          ref={inputRef}
          className="flex-1 outline-none leading-6"
          placeholder={selected ? selectedLabel : placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIdx(0);
          }}
          onKeyDown={onKeyDown}
        />
        {selected && (
          <span className="text-xs text-gray-600 whitespace-nowrap">
            {Number(selected.price || 0).toLocaleString()} Kesh / {selected.unit || "unit"}
          </span>
        )}
        <button
          type="button"
          className="text-gray-600 text-sm px-1"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
            inputRef.current?.focus();
          }}
          aria-label="Toggle product list"
        >
          ▾
        </button>
      </div>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto pc-scroll rounded border bg-white shadow-xl pr-1"
        >
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500">No matches</div>
          )}
          {filtered.map((p, i) => {
            const isActive = i === activeIdx;
            const isSelected = Number(p.product_id) === Number(value);
            return (
              <button
                key={p.product_id}
                data-idx={i}
                type="button"
                className={[
                  "w-full text-left px-3 py-2.5 text-sm",
                  isSelected
                    ? "bg-blue-600 text-white hover:bg-blue-600"
                    : isActive
                    ? "bg-blue-50"
                    : "hover:bg-blue-50",
                ].join(" ")}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => handlePick(p)}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={["mt-1 h-2.5 w-2.5 rounded-full", isSelected ? "bg-white" : "bg-blue-200"].join(" ")}
                  />
                  <div className="flex-1">
                    <div className="font-medium leading-5">
                      {p.name}
                      {(p.size || p.color) && (
                        <span className={isSelected ? "text-blue-100" : "text-gray-500"}>
                          {" "}- {p.size ? `size ${p.size}` : ""}{p.size && p.color ? " • " : ""}{p.color || ""}
                        </span>
                      )}
                    </div>
                    <div className={["text-xs", isSelected ? "text-blue-100" : "text-gray-500"].join(" ")}>
                      {metaText(p)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Main Page ------------------------------- */
const Sales = () => {
  const [form, setForm] = useState({ customer_name: "", payment_type: "cash" });
  const [amountPaid, setAmountPaid] = useState(0);
  const [lineItems, setLineItems] = useState([{ product_id: "", quantity: 1, current_price: 0 }]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [inventoryRes, customerRes] = await Promise.all([
          API.get("/inventory.php"),
          API.get("/customers.php"),
        ]);

        // NEW: normalize stock field so we always have .stock available
        const inv = (inventoryRes.data || []).map((p) => ({
          ...p,
          stock: typeof p.stock !== "undefined" ? Number(p.stock) : Number(p.quantity ?? 0),
        }));

        setProducts(inv);
        setCustomers(customerRes.data || []);
      } catch (e) {
        console.error(e);
        Swal.fire("❌ Load Error", "Could not load inventory or customers.", "error");
      }
    };
    load();
  }, []);

  const getProduct = (product_id) =>
    products.find((p) => Number(p.product_id) === Number(product_id)) || null;
  const getProductPrice = (product_id) => parseFloat(getProduct(product_id)?.price || 0);
  const getProductUnit = (product_id) => getProduct(product_id)?.unit || "unit";
  const getProductStock = (product_id) => {
    const p = getProduct(product_id);
    return p ? Number(p.stock ?? 0) : 0;
  };

  const handleLineChange = (index, key, value) => {
    setLineItems((prev) => {
      const items = [...prev];
      const updated = { ...items[index], [key]: value };
      if (key === "product_id") updated.current_price = getProductPrice(value);
      items[index] = updated;
      return items;
    });
  };

  const addLineItem = () =>
    setLineItems((prev) => [...prev, { product_id: "", quantity: 1, current_price: 0 }]);
  const removeLineItem = (index) => setLineItems((prev) => prev.filter((_, i) => i !== index));

  const handleCustomerSelect = (e) => {
    const selected = customers.find((c) => c.id.toString() === e.target.value);
    if (selected) setForm((prev) => ({ ...prev, customer_name: selected.name }));
  };

  const getTotal = () =>
    lineItems.reduce(
      (sum, item) => sum + parseFloat(item.current_price || 0) * (Number(item.quantity || 0) || 0),
      0
    );

  const printReceipt = (receiptHtml, { dateStr, timeStr, receiptNo } = {}) => {
    const headerBlock = `
      <div style="border-bottom:1px dashed #000; padding-bottom:6px; margin-bottom:8px;">
        <div><strong>Date:</strong> ${dateStr || ""}</div>
        <div><strong>Time:</strong> ${timeStr || ""}</div>
        <div><strong>Receipt #:</strong> ${receiptNo || ""}</div>
      </div>
    `;
    const win = window.open("", "", "width=400,height=600");
    win.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            @media print {
              @page { size: 80mm auto; margin: 0; }
              body { margin: 0; padding: 0; font-family: 'Courier New', monospace; font-size: 14px; width: 80mm; line-height: 1.4; }
            }
            body { font-family: 'Courier New', monospace; font-size: 14px; width: 80mm; padding: 12px; line-height: 1.4; }
            h3 { margin: 0 0 8px; padding: 0; font-size: 16px; font-weight: bold; text-align: center; }
            p { margin: 0 0 5px; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { text-align: left; padding: 5px; border-bottom: 1px dashed #000; }
            th { font-weight: bold; font-size: 14px; }
            .footer { text-align: center; margin-top: 12px; font-style: italic; font-size: 14px; }
          </style>
        </head>
        <body onload="window.print(); setTimeout(() => window.close(), 100)">
          ${headerBlock}
          ${receiptHtml || ""}
        </body>
      </html>
    `);
    win.document.close();
  };

  // ------------- NEW: read receipt number (sales.id) from the DB -------------
  const fetchLatestReceiptNo = async () => {
    try {
      const r = await API.get("/sales.php", { params: { recent: 1 } });
      const sale = r.data?.sale || r.data;
      return sale?.id ?? null;
    } catch (e) {
      console.error("❌ Could not fetch latest receipt no:", e);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || lineItems.length === 0)
      return Swal.fire("⚠️ Fill all required fields", "", "warning");

    // NEW: stock guard before we compute payload
    for (const item of lineItems) {
      if (!item.product_id) continue;
      const available = getProductStock(item.product_id);
      const requested = Number(item.quantity || 0);

      if (available <= 0) {
        return Swal.fire("❌ Out of stock", "Product out of stock.", "error");
      }
      if (requested > available) {
        return Swal.fire(
          "❌ Insufficient stock",
          `Requested ${requested} exceeds available stock (${available}).`,
          "error"
        );
      }
    }

    const validItems = lineItems
  .filter((item) => item.product_id && item.quantity > 0)
  .map((item) => {
    const product = getProduct(item.product_id);
    return {
      product_id: parseInt(item.product_id),
      quantity: item.quantity,
      unit_price: parseFloat(item.current_price || 0),
      unit: product?.unit || "unit",
      color: product?.color || null,     // ✅ ADD
      size: product?.size || null,       // ✅ OPTIONAL
      subtotal: parseFloat(item.current_price || 0) * item.quantity,
    };
  });


    if (validItems.length === 0)
      return Swal.fire("⚠️ Add at least one valid product", "", "warning");

    const totalAmount = getTotal();
    const paid = parseFloat(form.payment_type === "credit" ? amountPaid : totalAmount);
    const balance = totalAmount - paid;

    if (form.payment_type !== "credit" && Number(balance) !== 0)
      return Swal.fire("⚠️ Full payment required", "", "warning");

    const now = new Date();
    const dateStr = formatDate(now);
    const timeStr = formatTime(now);

    const payload = {
      ...form,
      items: validItems,
      amount_paid: paid,
      total_amount: totalAmount,
      balance,
      sale_datetime: now.toISOString(),
    };

    try {
      const res = await API.post("/sales.php", payload);
      const { receiptHtml } = res.data || {};

      let receiptNo =
        res.data?.receipt_number ??
        res.data?.sale_id ??
        res.data?.sale_item_id ??
        (Array.isArray(res.data?.saleItemIds) ? res.data.saleItemIds[0] : undefined) ??
        null;

      if (!receiptNo) {
        receiptNo = await fetchLatestReceiptNo();
      }

      printReceipt(receiptHtml, { dateStr, timeStr, receiptNo: receiptNo ?? "" });
      Swal.fire("✅ Sale completed", "Receipt printed", "success");

      setForm({ customer_name: "", payment_type: "cash" });
      setLineItems([{ product_id: "", quantity: 1, current_price: 0 }]);
      setAmountPaid(0);
    } catch (err) {
      console.error("❌ Submission error:", err.response?.data || err);
      Swal.fire("❌ Error", err.response?.data?.error || "Unexpected server error", "error");
    }
  };

  const total = getTotal();
  const balance =
    total - (form.payment_type === "credit" ? parseFloat(amountPaid || 0) : total);

  /* --------------------------------- UI --------------------------------- */
  return (
    <div className="relative w-full min-h-screen bg-gradient-to-br from-gray-100 to-gray-300 p-6">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20 blur-sm"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="relative z-10 max-w-4xl mx-auto bg-white p-8 rounded-2xl shadow-2xl border border-blue-100">
        <h2 className="text-3xl font-bold mb-6 text-center text-blue-700">🧾 PetrahCollection POS</h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <select onChange={handleCustomerSelect} className="w-full px-4 py-2 border rounded">
              <option value="">-- Select Existing Customer (optional) --</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.phone && `(${c.phone})`}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Customer Name"
              className="w-full px-4 py-2 border rounded"
              required
            />
          </div>

          {lineItems.map((item, index) => {
            const unit = getProductUnit(item.product_id);
            const expectedPrice = getProductPrice(item.product_id);
            return (
              <div
                key={index}
                className="flex flex-wrap gap-2 items-center border rounded p-3 bg-gray-50 shadow-sm"
              >
                <div className="flex-1 min-w-[240px]">
                  <ProductSelect
                    products={products}
                    value={item.product_id}
                    onChange={(pid) => handleLineChange(index, "product_id", parseInt(pid))}
                    placeholder="-- Select Product --"
                  />
                </div>

                <input
                  type="number"
                  min={1}
                  value={item.quantity}
                  onChange={(e) => handleLineChange(index, "quantity", parseInt(e.target.value))}
                  className="w-20 px-2 py-1 border rounded"
                />

                <input
                  type="number"
                  step="0.01"
                  min={0}
                  value={item.current_price}
                  onChange={(e) =>
                    handleLineChange(index, "current_price", parseFloat(e.target.value))
                  }
                  className="w-24 px-2 py-1 border rounded"
                />

                <span className="text-sm text-gray-600">
                  <strong>{unit}</strong>
                  <br />
                  <span className="text-xs text-gray-400">
                    Expected: {expectedPrice.toLocaleString()} Kesh
                  </span>
                </span>

                <button
                  type="button"
                  onClick={() => removeLineItem(index)}
                  className="text-red-500 text-xl font-bold"
                  aria-label="Remove line"
                >
                  ✖
                </button>
              </div>
            );
          })}

          <button
            type="button"
            onClick={addLineItem}
            className="text-blue-700 font-semibold hover:underline"
          >
            ➕ Add Product
          </button>

          <select
            value={form.payment_type}
            onChange={(e) => setForm({ ...form, payment_type: e.target.value })}
            className="w-full px-4 py-2 border rounded"
          >
            <option value="cash">Cash</option>
            <option value="momo">Momo</option>
            <option value="bank">Bank</option>
            <option value="credit">Credit</option>
          </select>

          {form.payment_type === "credit" && (
            <input
              type="number"
              step="0.01"
              min={0}
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              placeholder="Enter Amount Paid"
              className="w-full px-4 py-2 border rounded"
              required
            />
          )}

          {lineItems.some((item) => item.product_id) && (
            <div className="text-xs text-gray-500 text-right italic">
              * You may adjust price manually. Expected prices are pre-filled.
            </div>
          )}

          <div className="text-right font-semibold text-gray-800">
            <div>Total: {getTotal().toLocaleString()} Kesh</div>
            <div>
              Paid: {(form.payment_type === "credit" ? parseFloat(amountPaid || 0) : getTotal()).toLocaleString()} Kesh
            </div>
            <div>
              Balance: {(getTotal() - (form.payment_type === "credit" ? parseFloat(amountPaid || 0) : getTotal())).toLocaleString()} Kesh
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-blue-700 text-white py-3 rounded-xl hover:bg-blue-800 transition"
          >
            ✅ Complete Sale
          </button>
        </form>
      </div>
    </div>
  );
};

export default Sales;
